// Considered: TemplateDetailPageOrganism — rejected. It is channel-item-first
// (`{channelId, itemId}`), has no by-systemName read path, and its type map omits
// `aspect` entirely; its Reference/Fork CTAs are wrong for a read-only browse drawer.
// This organism composes the per-type by-systemName reads directly instead. See spec
// Design System Reuse Audit.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'localization';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { getArcTemplate, getAspectBySystemName, type ChannelItemLookupEntry } from '../../../api/marketplaceApi';
import { useApproachDetail } from '../../../hooks/useApproachDetail';
import type { StudioTemplateCard } from 'types/studio';
import type { ArcTemplate } from 'types/arc';
import type { AspectRow } from 'types/aspect';
import DrawerAtom from '../../atoms/Drawer/DrawerAtom';
import TagAtom from '../../atoms/Tag/TagAtom';
import { StatusBadgeAtom } from '../../atoms/StatusBadge';
import MessageAtom from '../../atoms/Message/MessageAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import WhatsIncludedReaderOrganism from '../marketplace/WhatsIncludedReaderOrganism';
import ArcStageListMolecule from '../../molecules/studio/ArcStageListMolecule';
import AspectSummaryMolecule from '../../molecules/studio/AspectSummaryMolecule';
import DistributionStatusMolecule from '../../molecules/studio/DistributionStatusMolecule';
import { studioStatusTone } from '../../molecules/studio/studioStatusTone';
import './StudioTemplateDrawerOrganism.scss';

export interface StudioTemplateDrawerOrganismProps {
  visible: boolean;
  onHide: () => void;
  /** The card from the gallery's own (already permission-filtered) list; `null` while the
   * gallery is still loading OR once loaded, if the requested systemName isn't in it —
   * both cases the caller distinguishes via `cardResolutionLoading`. */
  card: StudioTemplateCard | null;
  /** True while the gallery's own list fetch is still in flight. */
  cardResolutionLoading: boolean;
  /** Batched distribution-lookup result for this card; `null` = unresolved or failed. */
  distributionChannels: ChannelItemLookupEntry[] | null;
  distributionDegraded?: boolean;
}

type BodyStatus = 'loading' | 'ready' | 'notFound';

/**
 * Read-only detail drawer for a Studio browse-builder gallery card (EAINT-873 SC5). Each
 * type fetches through its own existing `assertTemplateViewable`-gated by-systemName read —
 * approach via `useApproachDetail` (already used by `ApproachDetailPageOrganism`), arc via
 * `getArcTemplate`, aspect via `getAspectBySystemName`. 403/404/503 all collapse to the same
 * localized not-found body (`renderNotFound`) so analytics and the UI never encode which.
 *
 * There is deliberately no `schema` case: schema cards never reach this drawer, because the
 * Approach gallery navigates them straight to the Sphere Type Editor
 * (`/studio/types/:schemaId`, EAINT-892) instead of opening it.
 */
const StudioTemplateDrawerOrganism: React.FC<StudioTemplateDrawerOrganismProps> = ({
  visible,
  onHide,
  card,
  cardResolutionLoading,
  distributionChannels,
  distributionDegraded = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const approachResult = useApproachDetail(card?.type === 'approach' ? card.systemName : null);

  const [arcTemplate, setArcTemplate] = useState<ArcTemplate | null>(null);
  const [arcStatus, setArcStatus] = useState<BodyStatus>('loading');
  const [aspectRow, setAspectRow] = useState<AspectRow | null>(null);
  const [aspectStatus, setAspectStatus] = useState<BodyStatus>('loading');

  useEffect(() => {
    if (card?.type !== 'arc_template' || !apiClients?.schemaClient) {
      setArcTemplate(null);
      setArcStatus('loading');
      return;
    }
    let cancelled = false;
    setArcStatus('loading');
    // Draft cards (draft-only arc families, owner decision 2026-09-01) need includeDrafts —
    // scoped to this one fetch and matched by the card's version; see getArcTemplate's doc.
    getArcTemplate(apiClients.schemaClient, card.systemName,
      card.status === 'draft' ? { includeDrafts: true, version: card.version ?? undefined } : undefined)
      .then((result) => {
        if (cancelled) return;
        if (!result) {
          setArcStatus('notFound');
          return;
        }
        setArcTemplate(result);
        setArcStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setArcStatus('notFound');
      });
    return () => {
      cancelled = true;
    };
  }, [apiClients?.schemaClient, card?.type, card?.systemName, card?.status, card?.version, organizationId]);

  useEffect(() => {
    if (card?.type !== 'aspect' || !apiClients?.schemaClient) {
      setAspectRow(null);
      setAspectStatus('loading');
      return;
    }
    let cancelled = false;
    setAspectStatus('loading');
    getAspectBySystemName(apiClients.schemaClient, card.systemName)
      .then((result) => {
        if (cancelled) return;
        if (!result) {
          setAspectStatus('notFound');
          return;
        }
        setAspectRow(result);
        setAspectStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setAspectStatus('notFound');
      });
    return () => {
      cancelled = true;
    };
  }, [apiClients?.schemaClient, card?.type, card?.systemName]);

  const renderNotFound = () => (
    <EmptyStateAtom
      icon="pi-ban"
      title={t('studio.gallery.drawer.notFound.title')}
      description={t('studio.gallery.drawer.notFound.description')}
    />
  );

  const renderLoading = () => (
    <div className="studio-template-drawer__skeleton" data-testid="studio-template-drawer-skeleton">
      <SkeletonAtom shape="rectangle" height="1.5rem" />
      <SkeletonAtom shape="rectangle" height="4rem" />
    </div>
  );

  const renderDistribution = () => {
    if (!card) return null;
    return (
      <div
        className="studio-template-drawer__distribution"
        data-testid="studio-template-drawer-distribution"
      >
        <DistributionStatusMolecule
          channels={distributionChannels}
          degraded={distributionDegraded}
          onNavigateToChannel={(channelId) =>
            navigate(`/marketplace/channels/${encodeURIComponent(channelId)}`)
          }
          // No onListInChannel — this is a read-only browse drawer.
        />
        {card.type === 'approach' && (distributionChannels?.length ?? 0) > 0 && (
          <button
            type="button"
            className="studio-template-drawer__marketplace-link"
            onClick={() => navigate(`/marketplace/approaches/${encodeURIComponent(card.systemName)}`)}
          >
            {t('studio.gallery.drawer.distribution.viewInMarketplace')}
          </button>
        )}
      </div>
    );
  };

  const renderBody = () => {
    if (!card) return null;
    switch (card.type) {
      case 'approach': {
        if (approachResult.loading && !approachResult.approach) return renderLoading();
        if (!approachResult.approach) return renderNotFound();
        return (
          <WhatsIncludedReaderOrganism
            systemName={card.systemName}
            version={approachResult.version?.version}
            depth={0}
          />
        );
      }
      case 'arc_template': {
        if (arcStatus === 'loading') return renderLoading();
        if (arcStatus === 'notFound' || !arcTemplate) return renderNotFound();
        return <ArcStageListMolecule stages={arcTemplate.definition?.stages ?? []} />;
      }
      case 'aspect': {
        if (aspectStatus === 'loading') return renderLoading();
        if (aspectStatus === 'notFound' || !aspectRow) return renderNotFound();
        return <AspectSummaryMolecule aspect={aspectRow.definition} />;
      }
      default:
        return null;
    }
  };

  const statusTone = card ? studioStatusTone(card.status) : null;
  const header = card ? (
    <div className="studio-template-drawer__header">
      <span className="studio-template-drawer__title">{card.displayName}</span>
      <div className="studio-template-drawer__header-chips">
        <TagAtom size="small" tone="outline" label={t(`studio.desk.templateType.${card.type}`)} />
        {statusTone && card.status && (
          <StatusBadgeAtom
            size="small"
            tone={statusTone}
            label={t(`studio.desk.status.${card.status}`)}
          />
        )}
        {card.version != null && (
          <span className="studio-template-drawer__version">
            {t('studio.gallery.drawer.versionLabel', { version: card.version })}
          </span>
        )}
      </div>
    </div>
  ) : (
    <span className="studio-template-drawer__title">{t('studio.gallery.drawer.title')}</span>
  );

  return (
    <DrawerAtom
      visible={visible}
      onHide={onHide}
      position="right"
      width="480px"
      header={header}
      className="studio-template-drawer"
    >
      <div className="studio-template-drawer__body" data-testid="studio-template-drawer-body">
        {cardResolutionLoading && renderLoading()}
        {!cardResolutionLoading && !card && renderNotFound()}
        {!cardResolutionLoading && card && (
          <>
            <MessageAtom
              severity="info"
              text={t('studio.gallery.readOnlyNotice')}
              className="studio-template-drawer__read-only-notice"
            />
            {renderDistribution()}
            <section
              className="studio-template-drawer__type-body"
              data-testid={`studio-template-drawer-body-${card.type}`}
            >
              {renderBody()}
            </section>
          </>
        )}
      </div>
    </DrawerAtom>
  );
};

StudioTemplateDrawerOrganism.displayName = 'StudioTemplateDrawerOrganism';

export default StudioTemplateDrawerOrganism;
